import React, { forwardRef } from 'react';
import ReactMarkdown, { Options as ReactMarkdownProps } from 'react-markdown';
import RemarkGFM from 'remark-gfm';
import RehypeRaw from 'rehype-raw';
import RehypeSanitize from 'rehype-sanitize';
import { SxProps, styled } from '../../utils';
import { Box } from '../Box';

type NativeDivProps = React.ComponentPropsWithoutRef<'div'>;

export interface MarkdownProps extends NativeDivProps {
  /**
   * Markdown to be converted to HTML.
   */
  children: ReactMarkdownProps['children'];

  /**
   * Props passed to react-markdown. This should only be needed rarely within libraries
   * like viz-framework that customize markdown behavior.
   */
  ReactMarkdownProps?: Omit<ReactMarkdownProps, 'children'>;

  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps;
}

const DEFAULT_REMARK_PLUGINS = [RemarkGFM];
const DEFAULT_REHYPE_PLUGINS = [RehypeRaw, RehypeSanitize];

/**
 * Renders HTML from github flavored markdown (GFM).
 */
export const Markdown = forwardRef<HTMLDivElement, MarkdownProps>(function Markdown(
  { sx, children, ReactMarkdownProps, ...otherProps },
  ref
) {
  return (
    <MarkdownWrapper ref={ref} sx={sx} {...otherProps}>
      <ReactMarkdown
        remarkPlugins={DEFAULT_REMARK_PLUGINS}
        rehypePlugins={DEFAULT_REHYPE_PLUGINS}
        {...ReactMarkdownProps}
      >
        {children}
      </ReactMarkdown>
    </MarkdownWrapper>
  );
});

const MarkdownWrapper = styled(Box)(({ theme }) => {
  return {
    // Make the content scrollable
    overflowY: 'auto',
    // Ignore top margin on the first element.
    '& :first-child': {
      marginTop: 0,
    },
    // Styles for headers
    '& h1': {
      fontSize: '2em',
    },
    // Styles for <code>
    '& code': {
      fontSize: '0.85em',
      color: theme.palette.text.primary,
    },
    '& :not(pre) code': {
      padding: '0.2em 0.4em',
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.primary,
      borderRadius: '4px',
    },
    '& pre': {
      padding: '1.2em',
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.primary,
      borderRadius: '4px',
      overflow: 'auto',
      whiteSpace: 'pre',
      margin: 0,
      display: 'block',
      minWidth: 'min-content',
      maxWidth: 'none',
    },
    // Styles for <table>
    '& table, & th, & td': {
      padding: '0.6em',
      border: `1px solid ${theme.palette.grey[300]}`,
      borderCollapse: 'collapse',
    },
    // Styles for <li>
    '& li + li': {
      marginTop: '0.25em',
    },
    // Styles for <a>
    '& a': {
      color: theme.palette.primary.main,
    },
  };
});
